import React from 'react';

import { useNavigate } from 'react-router-dom';

import { TextInput, SelectDropdown, CalenderInput, TextArea } from '../../../components/reusables/FormElements';
import AttachFiles from './file_management/AttachFiles';

import { IJobPostFormData, TJobPostFormField } from '../../../interfaces/job_posts';
import { ISelectDropdownOption } from '../../../interfaces/formElements';
import { IJobTitle, EtyLocation } from '../../../interfaces/entities';
import { IAttachedFileWithUrl, IEmploymentIndustry } from '../../../interfaces/services';

import { OPEmploymentTypes, OPJobLevels, OPOnsiteRemote, OPSalaryCurrencies, OPSalaryPeriods } from '../../../data/options';

interface IJobDetailsFormProps {
    formData: IJobPostFormData;
    isFormDisabled: boolean;
    attachedFiles: IAttachedFileWithUrl[];
    filesToUpload: File[];
    employmentIndustries: IEmploymentIndustry[];
    locations: EtyLocation[];
    occupationRecommendations: IJobTitle[];
    isOccupationRecommendationsLoading: boolean;
    isLoading: boolean;
    setFormData: React.Dispatch<React.SetStateAction<IJobPostFormData>>;
    setAttachedFiles: React.Dispatch<React.SetStateAction<IAttachedFileWithUrl[]>>;
    setFilesToUpload: React.Dispatch<React.SetStateAction<File[]>>;
}

const JobDetailsForm = (props: IJobDetailsFormProps) => {
    const { formData, attachedFiles, filesToUpload, employmentIndustries, locations, occupationRecommendations, isOccupationRecommendationsLoading, isLoading, isFormDisabled, setFormData, setAttachedFiles, setFilesToUpload } = props;
    const navigate = useNavigate();

    const onChangeText = (name: TJobPostFormField, value: string) => {
        setFormData({
            ...formData,
            [name]: { value, error: "" }
        });
    };

    const onChangeSelect = (name: TJobPostFormField, option: ISelectDropdownOption) => {
        setFormData(prevData => {
            return {
                ...prevData,
                [name]: { value: option, error: "" }
            };
        });
    };

    const isDisabled = isFormDisabled || isLoading;

    return (
        <div className='immployer__details_form_view'>
            <div className="immployer__details_form_inputs__container">
                <div className="immployer__details_form_inputs">
                    <TextInput
                        name="title"
                        type="text"
                        required={true}
                        label="Job Title"
                        placeholder='eg. Software Engineer'
                        errorMsg={formData.title.error}
                        onChange={(value: string) => onChangeText("title", value)}
                        value={formData.title.value}
                        length_flex={2}
                        read_only={isDisabled}
                    />
                    <SelectDropdown
                        name="occupation"
                        label="Occupation"
                        placeholder="-- select occupation --"
                        value={formData.occupation.value}
                        onChange={(option: ISelectDropdownOption) => onChangeSelect("occupation", option)}
                        options={occupationRecommendations.map((occupation: IJobTitle) => ({ value: occupation.job_title, label: occupation.noc_code.noc_code + ' - ' + occupation.job_title }))}
                        read_only={isDisabled || isOccupationRecommendationsLoading}
                    />
                    <SelectDropdown
                        name="industry_id"
                        label="Industry"
                        placeholder="-- select industry --"
                        value={formData.industry_id.value}
                        onChange={(option: ISelectDropdownOption) => onChangeSelect("industry_id", option)}
                        options={employmentIndustries.map((industry: IEmploymentIndustry) => ({ value: industry.id.toString(), label: industry.name }))}
                        read_only={isDisabled}
                    />
                    <TextInput
                        name="keywords"
                        type="text"
                        label="Keywords"
                        placeholder='eg. Software Engineer, Developer, etc.'
                        errorMsg={formData.keywords.error}
                        onChange={(value: string) => onChangeText("keywords", value)}
                        value={formData.keywords.value}
                        read_only={isDisabled}
                    />
                    <CalenderInput
                        name="advert_start"
                        label="Advert Start Date"
                        placeholder='mm/dd/yyyy'
                        errorMsg={formData.advert_start.error}
                        onChange={(value: string) => onChangeText("advert_start", value)}
                        value={formData.advert_start.value}
                        format="mm/dd/yyyy"
                        read_only={isDisabled}
                    />
                    <CalenderInput
                        name="advert_end"
                        label="Advert End Date"
                        placeholder='mm/dd/yyyy'
                        errorMsg={formData.advert_end.error}
                        onChange={(value: string) => onChangeText("advert_end", value)}
                        value={formData.advert_end.value}
                        format="mm/dd/yyyy"
                        read_only={isDisabled}
                    />
                    <CalenderInput
                        name="job_start"
                        label="Job Start Date"
                        placeholder='mm/dd/yyyy'
                        errorMsg={formData.job_start.error}
                        onChange={(value: string) => onChangeText("job_start", value)}
                        value={formData.job_start.value}
                        format="mm/dd/yyyy"
                        read_only={isDisabled}
                    />
                    <TextArea
                        name="description"
                        required={true}
                        label="Job Description"
                        placeholder='Paste your job description here'
                        errorMsg={formData.description.error}
                        onChange={(value: string) => onChangeText("description", value)}
                        value={formData.description.value}
                        rows={5}
                        length_flex={3}
                        read_only={isDisabled}
                    />
                    <TextArea
                        name="responsibilities"
                        label="Responsibilities"
                        placeholder='Paste your job responsibilities here'
                        errorMsg={formData.responsibilities.error}
                        onChange={(value: string) => onChangeText("responsibilities", value)}
                        value={formData.responsibilities.value}
                        rows={5}
                        length_flex={3}
                        read_only={isDisabled}
                    />
                    <TextArea
                        name="qualifications"
                        label="Qualifications"
                        placeholder='Paste your job qualifications here'
                        errorMsg={formData.qualifications.error}
                        onChange={(value: string) => onChangeText("qualifications", value)}
                        value={formData.qualifications.value}
                        rows={5}
                        length_flex={3}
                        read_only={isDisabled}
                    />
                    <SelectDropdown
                        name="location_id"
                        label="Company & Location"
                        required={true}
                        placeholder="-- select location --"
                        value={formData.location_id.value}
                        onChange={(option: ISelectDropdownOption) => onChangeSelect("location_id", option)}
                        errorMsg={formData.location_id.error}
                        options={locations.map((location: EtyLocation) => ({ value: location.id.toString(), label: `${location.identifier} - ${location.employer.legal_name}` }))}
                        read_only={isDisabled}
                        length_flex={2}
                        addNewBtnData={{
                            name: "Location",
                            label: "Add Location",
                            action: () => navigate(`/employers`),
                        }}
                    />
                    <TextInput
                        name="open_positions"
                        type="text"
                        label="Open Positions"
                        placeholder='eg. 10'
                        pattern_mask='integer'
                        errorMsg={formData.open_positions.error}
                        onChange={(value: string) => onChangeText("open_positions", value)}
                        value={formData.open_positions.value}
                        read_only={isDisabled}
                    />
                    <SelectDropdown
                        name="employment_type"
                        label="Type"
                        errorMsg={formData.employment_type.error}
                        onChange={(option: ISelectDropdownOption) => onChangeSelect("employment_type", option)}
                        value={formData.employment_type.value}
                        options={OPEmploymentTypes}
                        read_only={isDisabled}
                    />
                    <TextInput
                        name="experience_level"
                        type="text"
                        label="Experience Level"
                        placeholder='eg. 2 years, 3 - 5 years, etc.'
                        errorMsg={formData.experience_level.error}
                        onChange={(value: string) => onChangeText("experience_level", value)}
                        value={formData.experience_level.value}
                        read_only={isDisabled}
                    />
                    <SelectDropdown
                        name="job_level"
                        label="Job Level"
                        errorMsg={formData.job_level.error}
                        onChange={(option: ISelectDropdownOption) => onChangeSelect("job_level", option)}
                        value={formData.job_level.value}
                        options={OPJobLevels}
                        read_only={isDisabled}
                    />
                    <SelectDropdown
                        name="onsite_remote"
                        label="Work Location"
                        errorMsg={formData.onsite_remote.error}
                        onChange={(option: ISelectDropdownOption) => onChangeSelect("onsite_remote", option)}
                        value={formData.onsite_remote.value}
                        options={OPOnsiteRemote}
                        read_only={isDisabled}
                    />
                    <AttachFiles
                        attachedFiles={attachedFiles}
                        setAttachedFiles={setAttachedFiles}
                        filesToUpload={filesToUpload}
                        setFilesToUpload={setFilesToUpload}
                        isDisabled={isDisabled}
                    />
                    <TextInput
                        name="salary_min"
                        type="text"
                        label="Minimum Salary"
                        pattern_mask='integer'
                        placeholder='eg. $100,000'
                        errorMsg={formData.salary_min.error}
                        onChange={(value: string) => onChangeText("salary_min", value)}
                        value={formData.salary_min.value}
                        read_only={isDisabled}
                    />
                    <TextInput
                        name="salary_max"
                        type="text"
                        label="Maximum Salary"
                        pattern_mask='integer'
                        placeholder='eg. $120,000'
                        errorMsg={formData.salary_max.error}
                        onChange={(value: string) => onChangeText("salary_max", value)}
                        value={formData.salary_max.value}
                        read_only={isDisabled}
                    />
                    <SelectDropdown
                        name="salary_currency"
                        label="Currency"
                        errorMsg={formData.salary_currency.error}
                        onChange={(option: ISelectDropdownOption) => onChangeSelect("salary_currency", option)}
                        value={formData.salary_currency.value}
                        options={OPSalaryCurrencies}
                        read_only={isDisabled}
                    />
                    <SelectDropdown
                        name="salary_period"
                        label="Salary Period"
                        errorMsg={formData.salary_period.error}
                        onChange={(option: ISelectDropdownOption) => onChangeSelect("salary_period", option)}
                        value={formData.salary_period.value}
                        options={OPSalaryPeriods}
                        read_only={isDisabled}
                    />
                </div>
            </div>
        </div>
    )
}

export default JobDetailsForm;